import React, { useState, useRef, useEffect } from 'react';
import { Modal, Form, Input, Button, Row, Col } from 'antd';
import SuccessModal from '../../../../components/SuccessModal/SuccessModal';
import FloatingLabel from '../../../../components/FloatingLabel/FloatingLabel';
import EmailField from '../../../../components/SharedComponents/FormComponents/EmailField';
import OTPInput from '../../../../components/OTPInput';
import { CloseIcon } from '../../../../assets/icons';
import { verifyResetPinCodeApi, setNewPinApi } from '../../../../services/AccountApi/profileApi';
import { toastNotify } from '../../../../utils/toastNotify';
import { PIN_SECURITY_DESCRIPTION_WITH_WARNING } from '../../../../utils/constant';
import { useSelector } from 'react-redux';

const ResetPin = (props) => {
  // const { phoneNumber } = props;
  const [pin, setPin] = useState(undefined);
  const [confirmPin, setConfirmPin] = useState(undefined);
  const [email, setEmail] = useState();
  const [successOpen, setSuccessOpen] = useState(false);
  const [verification, setVerification] = useState('');
  const [step, setStep] = useState('step2');
  const [loading, setLoading] = useState(false);
  const userProfileData = useSelector((state) => state.userData.userData);
  const [form] = Form.useForm();
  // const nameValue = Form.useWatch('verification', form);
  const verificationRef = useRef();

  useEffect(() => {
    if (step === 'step3' && form.getFieldValue('pin')) {
      form.validateFields(['pin']);
    }
  }, [step, form]);

  const handleVerifyPin = async () => {
    setLoading(true);
    let varificationCode = {
      code: parseInt(verificationRef?.current?.getFieldValue('verification')),
    };
    try {
      const resData = await verifyResetPinCodeApi(varificationCode);
      if (!resData.isError) {
        toastNotify('success', resData?.message);
        setStep('step3');
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPin = async () => {
    if (pin === confirmPin) {
      setLoading(true);
      let newPin = {
        newPin: confirmPin,
      };
      try {
        const resData = await setNewPinApi(newPin);
        if (!resData.isError) {
          setSuccessOpen(true);
          setStep('')
        }
      } catch (error) {
        const { response } = error;
        toastNotify('error', response?.data?.message);
        setPin(undefined);
        setStep('step3');
      } finally {
        setLoading(false);
      }
    } else {
      toastNotify('error', 'PIN does not match.');
      setPin(undefined);
      setStep('step3');
    }
  };

  return (
    <>
      {step !== "" && 
      <Modal
        className='account__modal'
        open={props.setOpen}
        onCancel={() => {
          verificationRef?.current?.resetFields();
          props.setClose();
        }}
        width={524}
        footer={false}
        closeIcon={<CloseIcon />}
        centered
      >
        {step === 'step1' && (
          <>
            <h4 className='mb-32 account__modal__title'>Enter your email or phone number to reset 4-digit PIN</h4>
            <Form>
              <FloatingLabel label='Email or phone number' inputId='email' inputValue={email}>
                <EmailField className={'mb-48'} email={email} setEmail={(value) => setEmail(value)} />
              </FloatingLabel>
              <Button type='primary' className='w-100' onClick={() => setStep('step2')}>
                Continue
              </Button>
            </Form>
          </>
        )}
        {step === 'step2' && (
          <>
            <h4 className='mb-32 account__modal__title'>Enter your verification code</h4>
            <p className='mb-32 text-taupe'>
              We have sent a verification code to
              <span className='fw-600'>
                {userProfileData?.isPhoneVerified && props?.phoneNumber}
                {userProfileData?.isPhoneVerified && userProfileData?.isEmailVerified && ' and '}
                {userProfileData?.isEmailVerified && props?.mail}
              </span>
              . Please enter the code below.
            </p>
            <Form
              ref={verificationRef}
              form={verificationRef?.current}
              onValuesChange={(changedValues, allValues) => setVerification(changedValues?.verification)}
            >
              <FloatingLabel label='Verification code' inputId='verification' inputValue={verificationRef?.current?.getFieldValue('verification')}>
                <Form.Item
                  className='mb-48'
                  name='verification'
                  rules={[
                    {
                      required: true,
                      message: 'Please enter verification code.',
                    },
                    {
                      min: 6,
                      message: 'Must be at least 6 characters.',
                    },
                  ]}
                >
                  <Input 
                    id='verification' 
                    onChange={(e) => form.setFieldsValue({ verification: e.target.value })} 
                    maxLength={6}
                    onKeyPress={(event) => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </FloatingLabel>
              <Form.Item className='mb-0' shouldUpdate>
                <Button loading={loading} disabled={verification === '' || verification.length <= 5} type='primary' className='w-100' onClick={() => handleVerifyPin()}>
                  Continue
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
        {step === 'step3' && (
          <>
            <h4 className='mb-32 account__modal__title'>Create a new 4-digit PIN</h4>
            <p className='mb-40 text-taupe'>{PIN_SECURITY_DESCRIPTION_WITH_WARNING}</p>
            <Form className='signup-form pin-form'>
              <OTPInput length={4} onChangeOTP={(otp) => setPin(otp)} className='mb-48 otp' autoFocus inputClassName='otp__input' />
              <Form.Item className='mb-0'>
                <Button
                  type='primary'
                  className='w-100'
                  onClick={() => {
                    setStep('step4');
                    setConfirmPin(undefined);
                  }}
                  disabled={!pin || pin?.length < 4}
                >
                  Continue
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
        {step === 'step4' && (
          <>
            <h4 className='mb-32 account__modal__title'>Re-enter your PIN</h4>
            <p className='mb-40 text-taupe'>Enter the 4-digit PIN you previously entered to ensure it is a match.</p>
            <Form className='signup-form pin-form'>
              <OTPInput
                disabled={loading}
                length={4}
                onChangeOTP={(otp) => setConfirmPin(otp)}
                className='mb-48 otp'
                autoFocus
                inputClassName='otp__input'
              />
              <Row gutter={[16, 16]}>
                <Col span={24} md={8}>
                  <Button
                    className='w-100'
                    type='text'
                    onClick={() => {
                      setPin(undefined);
                      setStep('step3');
                    }}
                  >
                    Back
                  </Button>
                </Col>
                <Col span={24} md={16}>
                  <Button
                    className='w-100'
                    type='primary'
                    onClick={() => {
                      handleConfirmPin();
                    }}
                    loading={loading}
                    disabled={!confirmPin || confirmPin?.length < 4}
                  >
                    Reset PIN
                  </Button>
                </Col>
              </Row>
            </Form>
          </>
        )}
      </Modal>
      }
      <SuccessModal
        setOpen={successOpen}
        setClose={() => {
          setSuccessOpen(false);
          props.setClose();
          setStep('step2');
        }}
        setDone={() => {
          setSuccessOpen(false);
          props.setClose();
          setStep('step2');
        }}
      />
    </>
  );
};

export default ResetPin;
